import * as React from 'react';
import { useState, useEffect } from 'react';
import { TableRow, TableCell, Loader } from 'semantic-ui-react';
import { Button, Grid } from '@120wateraudit/envirio-components';
import { Feature } from '../../types/Feature';
import {
  useDeleteAccountFeatureMutation,
  useFeaturesForAccount
} from 'src/hooks/useFeatures';
import gql from 'graphql-tag';
import { apolloClient } from 'src/apolloClient';
import { toast } from 'react-toastify';
import { Account } from 'src/types/AccountManagementTypes';

interface Props {
  accountId: number;
}

const GET_ACCOUNT_QUERY = gql`
  query getAccount($id: Int!) {
    account(id: $id) {
      id
      isAccountDisabled
      isDemo
    }
  }
`;

const SYNC_ACCOUNT_MUTATION = gql`
  mutation syncAccount($account: UpdateAccountInputType!) {
    updateAccount(account: $account) {
      id
    }
  }
`;

export const columns = [
  {
    key: 'code',
    sortable: false,
    name: 'Code'
  },
  {
    key: 'name',
    sortable: false,
    name: 'Name'
  },
  {
    key: 'remove',
    sortable: false,
    name: 'Remove'
  }
];

export const FeaturesGrid = ({ accountId }: Props) => {
  const { accountFeatures: features, loading } = useFeaturesForAccount(
    accountId
  );
  const [accountDetails, setAccountDetails] = useState<Account | null>(null);

  const [deleteAccountFeature] = useDeleteAccountFeatureMutation();

  useEffect(() => {
    const fetchAccountDetails = async () => {
      try {
        const { data } = await apolloClient.query({
          query: GET_ACCOUNT_QUERY,
          variables: { id: accountId }
        });

        if (data && data.account) {
          setAccountDetails(data.account);
        }
      } catch (error) {
        toast.error('Error fetching account details. Please try again.');
      }
    };

    fetchAccountDetails();
  }, [accountId]);

  const syncAccount = async (variables: any) => {
    try {
      return await apolloClient.mutate({
        mutation: SYNC_ACCOUNT_MUTATION,
        variables
      });
    } catch (error) {
      toast.error('Error syncing account. Please try again.');
      throw error;
    }
  };

  const onDeleteClicked = async ({ featureId }: { featureId: number }) => {
    try {
      await deleteAccountFeature({
        variables: {
          accountId,
          featureId
        },
        refetchQueries: ['features', 'availableFeatures']
      });

      if (accountDetails) {
        await syncAccount({
          account: {
            id: accountId,
            isAccountDisabled: accountDetails.isAccountDisabled,
            isDemo: accountDetails.isDemo
          }
        });
      } else {
        await syncAccount({
          account: {
            id: accountId,
            isAccountDisabled: false,
            isDemo: false
          }
        });
      }
    } catch (error) {
      toast.error('Error removing feature. Please try again.');
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <Grid
      style={{}}
      displaySearch={false}
      columns={columns}
      data={features}
      renderRow={(feature: Feature) => (
        <TableRow key={feature.id}>
          <TableCell>{feature.code}</TableCell>
          <TableCell>{feature.name}</TableCell>
          <TableCell>
            <Button
              variant="error"
              onClick={async () =>
                await onDeleteClicked({ featureId: feature.id })
              }>
              Remove
            </Button>
          </TableCell>
        </TableRow>
      )}
    />
  );
};
